/**
 * AdminAccountsEnhanced - Enhanced Accounts/Customers list view with standardized table
 * 
 * Features:
 * - Server-side sorting on all columns
 * - Server-side filtering (status)
 * - Global search
 * - Multi-select with bulk actions
 * - Admin-only delete with confirmation
 */
import React, { useState, useCallback } from 'react';
import { useAdminTable } from '../../hooks/useAdminTable';
import AdminDataTable, {
  MonoCell,
  DateCell,
  BooleanCell,
  TruncatedCell,
  BulkActionToolbar,
} from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import {
  FilterBar,
  GlobalSearch,
  StatusFilter,
  ActiveFilterChips,
} from '../../components/admin/AdminTableFilters';
import {
  DeleteConfirmationModal,
} from '../../components/admin/BulkActionModals';
import { Button } from '../../components/ui/button';
import { crmAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Eye,
  Building2,
  Trash2,
  Phone,
  Globe,
  MapPin,
  Plus,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ShoppingCart,
} from 'lucide-react';

// ===================================================
// STATUS CONFIGURATION
// ===================================================

const ACCOUNT_STATUSES = [
  { value: 'true', label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'false', label: 'Inactive', color: 'bg-gray-100 text-gray-500', icon: XCircle },
];

// ===================================================
// MAIN COMPONENT
// ===================================================

const AdminAccountsEnhanced = ({ onViewCustomer, onCreateCustomer, onCreateOrder }) => {
  const { isAdmin } = useAuth();
  
  // Bulk action modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Fetch function for accounts
  const fetchAccounts = useCallback(async (params) => {
    return await crmAPI.getAccounts(params);
  }, []);
  
  // Use admin table hook
  const {
    data: accounts,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortBy,
    sortDir,
    handleSort,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedCount,
    density,
    setDensity,
    refresh,
  } = useAdminTable({
    fetchFn: fetchAccounts,
    defaultSortBy: 'created_at',
    defaultSortDir: 'desc',
    defaultPageSize: 25,
    filterKeys: ['is_active', 'owner_user_id'],
    transformResponse: (data) => ({
      items: data.accounts || data.items || [],
      total: data.total || 0
    })
  });
  
  // Handle bulk delete
  const handleBulkDelete = async (reason) => {
    setBulkActionLoading(true);
    try {
      const result = await crmAPI.bulkDeleteAccounts({
        ids: selectedIds,
        reason
      });
      
      if (result.deleted_count > 0) {
        toast.success(`Deleted ${result.deleted_count} account(s)`);
      }
      if (result.blocked_items?.length > 0) {
        toast.warning(`${result.blocked_items.length} account(s) could not be deleted`);
      }
      
      setShowDeleteModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete accounts');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '—';
    const parts = [
      address.city,
      address.state
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  };
  
  // Table columns definition
  const columns = [
    {
      key: 'company_name',
      header: 'Company',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-ivory-100 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-ivory-500" />
          </div>
          <div className="min-w-0">
            <button
              onClick={() => onViewCustomer?.(row.id)}
              className="font-medium text-ivory-800 hover:text-blue-600 hover:underline truncate block max-w-[200px]"
              title={value || row.name}
            >
              {value || row.name || '—'}
            </button>
          </div>
        </div>
      )
    },
    {
      key: 'phone',
      header: 'Phone',
      sortable: false,
      render: (value) => (
        <div className="flex items-center gap-1.5 text-ivory-600">
          {value ? (
            <>
              <Phone className="w-3.5 h-3.5" />
              <span className="text-sm">{value}</span>
            </>
          ) : (
            <span className="text-ivory-400">—</span>
          )}
        </div>
      )
    },
    {
      key: 'website',
      header: 'Website',
      sortable: false,
      render: (value) => (
        value ? (
          <a 
            href={value.startsWith('http') ? value : `https://${value}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-blue-600 hover:underline text-sm"
          >
            <Globe className="w-3.5 h-3.5" />
            <TruncatedCell value={value.replace(/^https?:\/\//, '')} maxWidth={150} />
          </a>
        ) : (
          <span className="text-ivory-400">—</span>
        )
      )
    },
    {
      key: 'billing_address',
      header: 'Location',
      sortable: false,
      render: (value) => (
        <div className="flex items-center gap-1.5 text-ivory-600">
          <MapPin className="w-3.5 h-3.5" />
          <span className="text-sm">{formatAddress(value)}</span>
        </div>
      )
    },
    {
      key: 'tax_exempt',
      header: 'Tax Exempt',
      sortable: false,
      render: (value) => <BooleanCell value={value} showIcon />
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (value) => <DateCell value={value} />
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewCustomer?.(row.id);
            }}
            title="View Details"
            data-testid={`view-account-${row.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];
  
  // Filter labels for chips
  const filterLabels = {
    is_active: 'Status',
    owner_user_id: 'Owner',
  };
  
  return (
    <div className="space-y-6" data-testid="admin-accounts-enhanced">
      {/* Header */}
      <AdminListHeader
        title="Customers"
        subtitle={total > 0 ? `${total.toLocaleString()} total accounts` : undefined}
        createLabel="Add Customer"
        onCreate={onCreateCustomer}
        canCreate={isAdmin}
        onRefresh={refresh}
        refreshing={loading}
        density={density}
        onDensityChange={setDensity}
      />
      
      {/* Filters */}
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearFilters}>
        <GlobalSearch
          value={search}
          onChange={setSearch}
          placeholder="Search company, phone, email..."
          className="flex-1 min-w-[250px]"
        />
        
        <StatusFilter
          value={filters.is_active}
          onChange={(v) => setFilter('is_active', v)}
          options={ACCOUNT_STATUSES}
          placeholder="All Statuses"
          className="w-[150px]"
          data-testid="account-status-filter"
        />
      </FilterBar>
      
      {/* Active filter chips */}
      <ActiveFilterChips
        filters={filters}
        filterLabels={filterLabels}
        onRemove={(key) => setFilter(key, null)}
        onClearAll={clearFilters}
      />
      
      {/* Bulk action toolbar */}
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
      >
        {/* Create Order for selected customer (P1) */}
        {selectedCount === 1 && onCreateOrder && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const selectedAccount = accounts.find(a => selectedIds.includes(a.id));
              if (selectedAccount) {
                onCreateOrder({
                  id: selectedAccount.id,
                  name: selectedAccount.name || selectedAccount.company_name,
                  email: selectedAccount.email,
                  phone: selectedAccount.phone,
                });
              }
            }}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            data-testid="create-order-for-customer-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Create Order
          </Button>
        )}
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </BulkActionToolbar>
      
      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      {/* Data Table */}
      <AdminDataTable
        tableId="customers"
        data={accounts}
        columns={columns}
        loading={loading}
        emptyMessage="No customers found"
        emptyIcon={Building2}
        
        // Selection
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(row) => row.id}
        
        // Sorting
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        
        // Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        
        // Density
        density={density}
        onDensityChange={setDensity}
        
        // Column preferences
        showColumnSelector={true}
        resizableColumns={true}
        
        // Row click opens detail
        onRowClick={(row) => onViewCustomer?.(row.id)}
        
        data-testid="accounts-data-table"
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedCount}
        itemType="customer"
        loading={bulkActionLoading}
        warnings={[
          'Soft-deleted accounts can be restored by an administrator.',
          'Accounts with active orders cannot be deleted.'
        ]}
        requireReason={true}
      />
    </div>
  );
};

export default AdminAccountsEnhanced;
